import React, { useContext } from 'react'
import styled from 'styled-components'
import Modal from '@jarvis.oddle/core/components/Modal'
import { H2, LinkText, P } from '@jarvis.oddle/core/components/Typo'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import { paddingSizes, marginSizes } from '@jarvis.oddle/core/theme/default'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import Flex from '@jarvis.oddle/core/components/Flex'
import Form from '@jarvis.oddle/core/components/Form'
import {
  getCountryNameByDialCode,
  getShortCountryName
} from 'src/utils/localisation'
import { getPhoneError } from '@jarvis.oddle/core/tools/validation'
import IconBack from '@jarvis.oddle/core/components/Icons/IconBack'
import { updateUserAction, selectUser } from 'src/redux/user'
import useAppSelector from 'src/hooks/useAppSelector'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import { updateCheckoutMetaAction } from 'src/redux/checkout'
import { useRouter } from 'next/router'

const Container = styled(Modal.FlexContainer).attrs(() => ({
  width: '100%',
  height: '100%',
  flexDirection: 'column',
  padding: paddingSizes.medium
}))`
  position: relative;
  background: ${(props) => props.theme.colorContainerColor};
  width: 95%;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  transform: translateX(-50%) translateY(-50%);
  top: 50%;
  left: 50%;
  height: fit-content;
  ${breakpoint('desktop')`
    width: 100%;
    max-width: 600px;
    height: 100%;
    max-height: 492px;
  `}
`

const StyledTitle = styled(H2)`
  text-align: center;
  margin-top: 20px;
`

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const StyledSubmitButton = styled(Form.SubmitButton)`
  width: 50%;
`

const BackContainer = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  role: 'button'
}))`
  margin-top: 20px;
  color: ${(props) =>
    props.invert ? props.theme.buttonColor : props.theme.buttonTextColor};
  display: flex;
  cursor: pointer;
  justify-content: center;
  align-items: center;
`

const StyledLinkText = styled(LinkText).attrs((props) => ({
  color: props.invert ? props.theme.buttonColor : props.theme.buttonTextColor
}))``

const StyledIconBack = styled(IconBack).attrs((props) => ({
  fill: props.invert ? props.theme.buttonColor : props.theme.buttonTextColor
}))``

const StyledCheckbox = styled(Form.Checkbox)`
  margin-top: -10px;
`

interface ForeignPhoneNumberModalProps {
  modal: {
    show: boolean
    onClose: (x?: () => void) => void
  }
  dialCode: string
  customerPhone: string
  setPhoneNumber: (phone: string) => void
}

const ForeignPhoneNumberModal = ({
  modal,
  dialCode,
  customerPhone,
  setPhoneNumber
}: ForeignPhoneNumberModalProps): JSX.Element => {
  const countryName = getCountryNameByDialCode(dialCode)
  const { t } = useContext(TranslationContext)
  const user = useAppSelector(selectUser).data
  const dispatch = useAppDispatch()
  const router = useRouter()

  const onValidate = (values) => {
    const { phone } = values
    const errors = {} as {
      phone?: string
    }

    const phoneError = getPhoneError(t, phone)
    if (phoneError) {
      errors.phone = phoneError
    } else if (!phone.includes(dialCode)) {
      errors.phone = t('Please use _countryName_ phone number', { countryName })
    }

    return errors
  }

  const onSubmit = async (values, actions) => {
    const { phone, saveContact } = values
    if (saveContact) {
      const responseUpdateUserAction = await dispatch(
        updateUserAction({ ...user, phone })
      ).unwrap()
      const responseUpdateCheckoutMetaAction = await dispatch(
        updateCheckoutMetaAction({
          storeOrderId: router.query.storeOrderId,
          payload: {
            recipientContact: phone
          }
        })
      ).unwrap()
      if (
        responseUpdateUserAction.payload &&
        responseUpdateUserAction.errorCode === null &&
        responseUpdateCheckoutMetaAction.data &&
        responseUpdateCheckoutMetaAction.errors === null
      ) {
        showNotificationToast.success(
          t('Contact number for this order has been successfully updated')
        )
        setPhoneNumber(phone)
        actions.setSubmitting(false)
        modal.onClose()
        return
      }
      actions.setSubmitting(false)
      // will be sent to OMS later, once the api is ready.
    } else {
      const response = await dispatch(
        updateCheckoutMetaAction({
          storeOrderId: router.query.storeOrderId,
          payload: {
            recipientContact: phone
          }
        })
      ).unwrap()
      if (response.data && response.errors === null) {
        showNotificationToast.success(
          t('Contact number for this order has been successfully updated')
        )
        actions.setSubmitting(false)
        modal.onClose()
        return
      }
      actions.setSubmitting(false)
    }
  }

  const handleClick = () => {
    modalController.showModal('CheckoutExitConfirmationModal', {
      onConfirm: () => {
        window.location.href = '/'
      },
      withHeader: false
    })
  }

  return (
    <Modal
      {...modal}
      show={modal.show}
      slideInAnimation='right'
      closeOnBackdropClick={false}
      closeOnEscapeKeyPress={false}
    >
      <Container>
        <StyledTitle>{t('Let us know how we can reach you')}</StyledTitle>
        <P>
          {t(
            "Your current phone number is a _currentPhoneCountry_ based number _currentPhoneNumber_. We'll need a _country_ based contact in case our logistics partners need to reach you. ",
            {
              currentPhoneNumber: customerPhone,
              country: countryName,
              currentPhoneCountry:
                customerPhone &&
                (getCountryNameByDialCode(customerPhone.substring(0, 3)) ||
                  getCountryNameByDialCode(customerPhone.substring(0, 4)))
            }
          )}
        </P>
        {countryName && (
          <StyledForm
            onValidate={onValidate}
            initialValues={{ phone: '', saveContact: true }}
            onSubmit={onSubmit}
          >
            <Form.Phone
              defaultCountryCode={getShortCountryName(countryName)}
              type='phone'
              name='phone'
              label={t('Phone Number')}
              required
            />
            <StyledCheckbox name='saveContact'>
              {({ checked, disabled }) => (
                <React.Fragment>
                  <Form.Checkbox.Tick checked={checked} disabled={disabled} />
                  <P>{t('Use this contact for all future orders')}</P>
                </React.Fragment>
              )}
            </StyledCheckbox>
            <StyledSubmitButton>{t('Next')}</StyledSubmitButton>
          </StyledForm>
        )}
        <BackContainer
          onClick={handleClick}
          marginBottom={marginSizes.extraLarge}
        >
          <StyledIconBack invert />
          <StyledLinkText bold normalised marginLeft={marginSizes.small} invert>
            {t('Continue Shopping')}
          </StyledLinkText>
        </BackContainer>
      </Container>
    </Modal>
  )
}

export default makeControlledModal(
  ForeignPhoneNumberModal,
  'ForeignPhoneNumberModal'
)
